import type { Message } from '../hooks/useChat.ts';
import { sourceLines, toolLabel } from '../lib/format.ts';
import { Markdown } from '../lib/markdown.tsx';

const errorText: Record<NonNullable<Message['error']>, string> = {
  network: 'Se ha cortado la conexión. Comprueba la cobertura e inténtalo de nuevo.',
  rate_limit: 'Demasiadas preguntas seguidas. Espera un minuto y vuelve a intentarlo.',
  disabled: 'El asistente no está disponible ahora mismo.',
  server: 'No he podido responder ahora mismo. Inténtalo de nuevo en un momento.',
  answer: '',
};

/** Three old-gold dots, while the answer is on its way. */
function Typing({ label }: { label: string }) {
  return (
    <p className="flex items-center gap-2.5 text-base text-ink-soft dark:text-cream/75">
      <span className="flex gap-1" aria-hidden>
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="h-1.5 w-1.5 animate-dot rounded-full bg-gold"
            style={{ animationDelay: `${i * 0.15}s` }}
          />
        ))}
      </span>
      {label}
    </p>
  );
}

export function MessageView({ message, onRetry }: { message: Message; onRetry?: () => void }) {
  if (message.role === 'user') {
    return (
      <div className="flex animate-rise justify-end">
        <p className="max-w-[85%] rounded-2xl rounded-br-md bg-purple px-4 py-2.5 text-[1.05rem] leading-snug whitespace-pre-wrap text-cream dark:bg-purple-500">
          {message.text}
        </p>
      </div>
    );
  }

  const waiting = message.status === 'streaming' && !message.text;
  const failed = message.status === 'error';
  return (
    <article
      className="max-w-full animate-rise rounded-2xl rounded-bl-md border border-purple/10 bg-paper px-4 py-3 text-[1.05rem] leading-relaxed dark:border-cream/10 dark:bg-night-800"
      aria-busy={message.status === 'streaming'}
    >
      {waiting && <Typing label={message.tool ? toolLabel(message.tool) : 'Pensando…'} />}
      {message.text && <Markdown text={message.text} />}
      {failed && message.error !== 'answer' && (
        <p className="text-burgundy dark:text-burgundy-300">
          {errorText[message.error ?? 'server']}
        </p>
      )}
      {failed && onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-2 min-h-11 rounded-full border border-purple px-5 text-base font-semibold text-purple transition-colors hover:bg-purple hover:text-cream active:bg-purple-700 dark:border-gold dark:text-gold-300 dark:hover:bg-gold dark:hover:text-night"
        >
          Reintentar
        </button>
      )}
      {message.status === 'done' && (message.sources?.length || message.revised) && (
        <footer className="mt-3 space-y-0.5 border-t border-gold/40 pt-2 text-[0.8rem] leading-snug text-ink-soft dark:border-gold/25 dark:text-cream/65">
          {sourceLines(message.sources ?? []).map((line) => (
            <p key={line.text} title={line.detail}>
              Fuente: {line.text}
            </p>
          ))}
          {message.revised && <p>Respuesta revisada tras comprobar los datos.</p>}
        </footer>
      )}
    </article>
  );
}
